import { LoaderPinwheel, Spade, Cherry, ChevronRight, type LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";

export type GameKey = "wheel" | "blackjack" | "slot";

/** One icon family for the three games (the emojis used to stand in for icons). */
export const GAME_ICONS: Record<GameKey, LucideIcon> = {
  wheel: LoaderPinwheel,
  blackjack: Spade,
  slot: Cherry,
};

interface GameTileProps {
  game: GameKey;
  label: string;
  onClick: () => void;
  /** The first available game is the main call to action and gets the brand fill. */
  primary?: boolean;
  className?: string;
  style?: React.CSSProperties;
}

/**
 * GameTile — a game entry on the landing page. The main game is a solid brand
 * tile; the others are quiet surface tiles, so the choice reads at a glance.
 */
export const GameTile = ({ game, label, onClick, primary = false, className, style }: GameTileProps) => {
  const Icon = GAME_ICONS[game];
  return (
    <button
      type="button"
      onClick={onClick}
      style={style}
      className={cn(
        "pressable flex w-full items-center gap-3.5 rounded-[22px] p-3 pr-4 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ring-offset-background",
        primary
          ? "bg-primary text-primary-foreground shadow-brand hover:brightness-[1.04]"
          : "surface-tile text-foreground hover:border-primary/40",
        className,
      )}
    >
      <span
        className={cn(
          "flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl",
          primary ? "bg-primary-foreground/15 text-primary-foreground" : "bg-primary text-primary-foreground",
        )}
      >
        <Icon className="h-6 w-6" strokeWidth={2.2} aria-hidden="true" />
      </span>
      <span className="font-display flex-1 text-[17px] font-bold leading-tight">{label}</span>
      <ChevronRight
        className={cn("h-5 w-5 shrink-0", primary ? "text-primary-foreground/70" : "text-muted-foreground")}
        aria-hidden="true"
      />
    </button>
  );
};

export default GameTile;
